import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { X, Loader2, Mic, Volume2, Users, Play, Pause, Trash2 } from "lucide-react";

function fmtTime(seconds) {
  if (seconds == null || isNaN(seconds)) return "";
  const s = Math.max(0, Math.floor(seconds));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

// Stable colour per speaker label so SPEAKER_0 / SPEAKER_1 always look the same.
const SPK_COLORS = [
  "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400",
  "border-sky-500/40 bg-sky-500/5 text-sky-700 dark:text-sky-400",
  "border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400",
  "border-fuchsia-500/40 bg-fuchsia-500/5 text-fuchsia-700 dark:text-fuchsia-400",
  "border-rose-500/40 bg-rose-500/5 text-rose-700 dark:text-rose-400",
];

function colorFor(speaker) {
  if (!speaker) return SPK_COLORS[0];
  const m = String(speaker).match(/(\d+)/);
  const idx = m ? parseInt(m[1], 10) % SPK_COLORS.length : 0;
  return SPK_COLORS[idx];
}

export default function VoiceTranscriptModal({ recordingId, onClose, onDeleted }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/voice/recordings/${recordingId}`);
      setDoc(data);
      return data;
    } catch (err) {
      toast.error("Could not load recording");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingId]);

  // Poll while still transcribing
  useEffect(() => {
    if (!doc) return;
    const active = doc.status === "uploading" || doc.status === "transcribing";
    if (!active) return;
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.status]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setPlaying(true); }
    else { el.pause(); setPlaying(false); }
  };

  const seekTo = (sec) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = sec;
    el.play();
    setPlaying(true);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this recording and its transcript?")) return;
    try {
      await api.delete(`/voice/recordings/${recordingId}`);
      toast.success("Recording deleted");
      onDeleted?.();
      onClose();
    } catch {
      toast.error("Delete failed");
    }
  };

  // Memo so the `segments` array identity is stable across renders — keeps
  // the `speakerLabels` useMemo below honest. Without this, every render
  // creates a fresh `[]` fallback and invalidates downstream memos.
  const segments = React.useMemo(() => doc?.segments || [], [doc?.segments]);
  const isReady = doc?.status === "ready";
  const isFailed = doc?.status === "failed";

  // When the call has exactly two speakers, infer which one is the user vs the
  // assistant. Our turn-taking design guarantees the user speaks first, so the
  // speaker whose earliest segment has the smaller start-time is "You" and the
  // other is the AI ("Faro").
  const speakerLabels = React.useMemo(() => {
    if (!isReady || segments.length === 0) return null;
    const earliest = new Map();
    for (const s of segments) {
      const k = s.speaker || "SPEAKER";
      const t = typeof s.start === "number" ? s.start : Infinity;
      if (!earliest.has(k) || t < earliest.get(k)) earliest.set(k, t);
    }
    const ordered = [...earliest.entries()].sort((a, b) => a[1] - b[1]);
    if (ordered.length !== 2) return null;
    return { [ordered[0][0]]: "You", [ordered[1][0]]: "Faro" };
  }, [isReady, segments]);

  const labelFor = (spk) => speakerLabels?.[spk] || spk || "SPEAKER";

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-md grid place-items-center p-4" data-testid="voice-transcript-modal">
      <div className="w-full max-w-3xl border border-border bg-card max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">[ voice call transcript ]</div>
            <div className="font-display font-bold tracking-tight text-lg mt-1 truncate">
              Diarized transcript
              {doc?.transcript_language && (
                <span className="ml-2 text-xs font-mono text-muted-foreground uppercase">· {doc.transcript_language}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isReady && (
              <Button variant="ghost" size="icon" onClick={handleDelete} data-testid="voice-transcript-delete" title="Delete recording">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="voice-transcript-close">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Audio player (only when ready) */}
        {isReady && doc?.audio_url && (
          <div className="border-b border-border px-5 py-3 flex items-center gap-3 bg-secondary/30">
            <Button
              size="icon"
              variant="outline"
              className="rounded-full h-10 w-10"
              onClick={togglePlay}
              data-testid="voice-transcript-play"
              aria-label={playing ? "Pause playback" : "Play recording"}
            >
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-mono text-muted-foreground flex items-center gap-3">
                <Volume2 className="h-3.5 w-3.5" />
                <span>{fmtTime(currentTime)} / {fmtTime(doc.duration_seconds)}</span>
                {doc.num_speakers != null && (
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" />{doc.num_speakers} speakers</span>
                )}
              </div>
              <audio
                ref={audioRef}
                src={doc.audio_url}
                preload="metadata"
                onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                onEnded={() => setPlaying(false)}
                className="w-full mt-1"
                controls
                data-testid="voice-transcript-audio"
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div className="overflow-auto p-5 flex-1">
          {loading && (
            <div className="grid place-items-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}

          {!loading && !isReady && !isFailed && (
            <div className="grid place-items-center py-16 text-center">
              <Mic className="h-6 w-6 text-primary mb-3" />
              <div className="font-mono uppercase tracking-wider text-xs text-muted-foreground">{doc?.status || "processing"}</div>
              <div className="mt-2 text-sm text-muted-foreground">
                {doc?.sub_status || "Tensornex is transcribing and diarizing your call. This usually takes 20–60 seconds."}
              </div>
              <Loader2 className="h-5 w-5 animate-spin mt-4 text-muted-foreground" />
            </div>
          )}

          {isFailed && (
            <div className="border border-destructive/40 bg-destructive/5 p-4 rounded-sm text-sm">
              <div className="font-mono uppercase tracking-wider text-xs text-destructive mb-1">transcription failed</div>
              <div className="text-muted-foreground">{doc?.error || "Unknown error"}</div>
            </div>
          )}

          {isReady && segments.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-12">
              The recording was transcribed, but no speech was detected.
            </div>
          )}

          {isReady && segments.length > 0 && (
            <>
              {speakerLabels && (
                <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="speaker-legend">
                  <span>Speakers:</span>
                  {Object.entries(speakerLabels).map(([spk, label]) => (
                    <span key={spk} className={`px-1.5 py-0.5 border rounded-sm ${colorFor(spk)}`}>
                      <span className="font-semibold">{label}</span>
                      <span className="opacity-60 ml-1.5">{spk}</span>
                    </span>
                  ))}
                </div>
              )}
              <ul className="space-y-3" data-testid="voice-transcript-segments">
                {segments.map((s, i) => (
                  <li key={i} className={`border rounded-sm p-3 ${colorFor(s.speaker)}`}>
                    <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider mb-1.5">
                      <span className="font-semibold">
                        {labelFor(s.speaker)}
                        {speakerLabels && speakerLabels[s.speaker] && (
                          <span className="opacity-50 ml-2">{s.speaker}</span>
                        )}
                      </span>
                      {s.start != null && (
                        <button
                          className="opacity-70 hover:opacity-100 transition"
                          onClick={() => seekTo(s.start)}
                          data-testid={`segment-seek-${i}`}
                          title="Jump to this moment"
                        >
                          {fmtTime(s.start)}{s.end != null ? ` – ${fmtTime(s.end)}` : ""}
                        </button>
                      )}
                    </div>
                    <p className="text-sm leading-relaxed text-foreground">{(s.text || "").trim()}</p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
